import { z } from 'zod';
import { branchContactTypeSchema } from '../aggregated/branchContact';
import { PartnerSlimSchema } from '../partners/partners';

const addContainersSchemaErrorMap: z.core.$ZodErrorMap = (issue) => {
  // Use custom message if provided, otherwise fall back to default
  if (issue.message) {
    return { message: issue.message };
  }

  if (issue.path?.includes('location')) {
    return { message: 'add.container.step1.location.error' };
  }

  if (issue.path?.includes('contactInformation') && issue.path.includes('name')) {
    return { message: 'add.container.step2.order-details.contact-information.name.error' };
  }

  if (issue.path?.includes('contactInformation') && issue.path.includes('phone')) {
    return { message: 'add.container.step2.order-details.contact-information.phone.error' };
  }

  if (issue.path?.includes('orderDetails') && issue.path.includes('contactInformation')) {
    return { message: 'add.container.step2.order-details.contact-information.error' };
  }

  return { message: 'add.container.error.submit' };
};

export const loadAddContainersSchemaErrorMap = () => {
  z.config({ customError: addContainersSchemaErrorMap });
};

export const ContainerSchema = z.object({
  id: z.uuid(),
  containerName: z.string().optional(),
  containerType: z.string().optional(),
  otherLoadCarrierType: z.string().optional(),
  otherLoadCarrierName: z.string().optional(),
  material: z.string().optional(),
  materialName: z.string().optional(),
  materialOther: z.string().optional(),
  amount: z.number().max(100).min(1).optional(),
  palletCollarAmount: z.number().min(0).max(100).optional(),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format')
    .optional(),
  imageUrl: z.string().optional(),
});

export const OrderDetailsSchema = z.object({
  signing: z.boolean().default(false),
  contactInformation: z
    .object({
      name: z.string().min(1, 'add.container.step2.order-details.contact-information.name.error'),
      phone: z.string().min(1, 'add.container.step2.order-details.contact-information.phone.error'),
      email: z
        .union([z.literal(''), z.email('add.container.step2.order-details.email.error')])
        .optional(),
    })
    .optional(),
  additionalRecipient: z
    .union([z.literal(''), z.email('add.container.step2.order-details.email.error')])
    .optional(),
  comment: z.string().max(200).optional(),
  fileAttachments: z
    .array(
      z.object({
        id: z.string(),
        fileName: z.string(),
      }),
    )
    .optional(),
});

export const AddContainersSchema = z.object({
  location: z.object({ dwKey: z.string().min(1, 'add.container.step1.location.error') }),
  containers: z.array(ContainerSchema).min(1, 'add.container.step1.add-container.error'),
  orderDetails: OrderDetailsSchema,
});

export const AddContainersResponseSchema = AddContainersSchema.extend({
  transactionId: z.uuid(),
  createdDate: z.string(),
});

export const AddContainersEmailSchema = AddContainersSchema.extend({
  createdDate: z.string(),
  requestedBy: z.object({
    name: z.string().min(1),
    email: z.email(),
    phone: z.string(),
  }),
  location: PartnerSlimSchema,
  branchContact: branchContactTypeSchema.pick({ name: true, email: true }),
  toAdmin: z.boolean().default(false),
});

export type AddContainerOrderDetails = z.infer<typeof OrderDetailsSchema>;
export type AddContainers = z.infer<typeof AddContainersSchema>;
export type AddContainersResponse = z.infer<typeof AddContainersResponseSchema>;
export type NewContainer = z.infer<typeof ContainerSchema>;
export type AddContainersEmail = z.infer<typeof AddContainersEmailSchema>;
